import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ComputerOutlinedIcon from '@mui/icons-material/ComputerOutlined';
import { useTranslation } from '../../app/LanguageContext';
import { downloadDesktopRelease, getDesktopReleases } from '../../app/api';
import DesktopReleaseTimeline from './DesktopReleaseTimeline';

const DOWNLOAD_PLATFORMS = [
  {
    id: 'windows_msi',
    labelKey: 'extensions.eagle_desktop.platform_windows_installer',
    variant: 'contained',
  },
  {
    id: 'linux',
    labelKey: 'extensions.eagle_desktop.platform_linux',
    variant: 'outlined',
  },
];

function isRecommendedPlatform(platformId, detectedPlatform) {
  if (detectedPlatform === 'windows' && platformId === 'windows_msi') return true;
  if (detectedPlatform === 'linux' && platformId === 'linux') return true;
  return false;
}

function detectPlatform() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
  if (/Linux/i.test(platform) || /Linux/i.test(ua)) return 'linux';
  return 'windows';
}

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function DownloadButton({ platformId, labelKey, recommended, variant, latestVersion, latestRelease, downloading, onDownload, translate }) {
  const info = latestRelease.platforms?.[platformId];
  const available = info?.available;
  const key = `${latestVersion}-${platformId}`;
  return (
    <Button
      variant={variant}
      disabled={!available || downloading === key}
      startIcon={downloading === key ? <CircularProgress size={18} color="inherit" /> : <DownloadOutlinedIcon />}
      onClick={() => onDownload(latestVersion, platformId)}
      sx={{ flex: 1, justifyContent: 'flex-start' }}
    >
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="button" display="block">
          {translate(labelKey)}
          {recommended ? ` (${translate('extensions.eagle_desktop.recommended')})` : ''}
        </Typography>
        <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
          {available
            ? formatBytes(info.size_bytes)
            : translate('extensions.eagle_desktop.platform_unavailable')}
        </Typography>
      </Box>
    </Button>
  );
}

export default function DesktopDownloadsPage() {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const detectedPlatform = useMemo(() => detectPlatform(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getDesktopReleases();
      setData(res);
    } catch (err) {
      setError(err.message || translate('common.error'));
    } finally {
      setLoading(false);
    }
  }, [translate]);

  useEffect(() => {
    load();
  }, [load]);

  const latestRelease = data?.releases?.[0] || null;
  const latestVersion = data?.latest_version || latestRelease?.version || '';

  const handleDownload = async (version, platform) => {
    if (version !== latestVersion) {
      setError(translate('extensions.eagle_desktop.download_latest_only'));
      return;
    }
    const key = `${version}-${platform}`;
    setDownloading(key);
    try {
      await downloadDesktopRelease(version, platform);
    } catch (err) {
      setError(err.message || translate('common.error'));
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <ComputerOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" fontWeight={600}>
          {translate('extensions.eagle_desktop.page_title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('extensions.eagle_desktop.page_subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {latestRelease ? (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {translate('extensions.eagle_desktop.latest_version', { version: latestVersion })}
              </Typography>
              <Chip size="small" color="primary" label={translate('extensions.eagle_desktop.latest_badge')} />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {translate('extensions.eagle_desktop.download_hint')}
            </Typography>
            {detectedPlatform === 'windows' && (
              <Box sx={{ mb: 2, pl: 2, borderLeft: 3, borderColor: 'primary.main' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  {translate('extensions.eagle_desktop.windows_steps_title')}
                </Typography>
                <Typography component="ol" variant="body2" color="text.secondary" sx={{ m: 0, pl: 2.5 }}>
                  <li>{translate('extensions.eagle_desktop.windows_step_1')}</li>
                  <li>{translate('extensions.eagle_desktop.windows_step_2')}</li>
                  <li>{translate('extensions.eagle_desktop.windows_step_3')}</li>
                  <li>{translate('extensions.eagle_desktop.windows_step_4')}</li>
                </Typography>
              </Box>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
              {DOWNLOAD_PLATFORMS.map((p) => (
                <DownloadButton
                  key={p.id}
                  platformId={p.id}
                  labelKey={p.labelKey}
                  recommended={isRecommendedPlatform(p.id, detectedPlatform)}
                  variant={isRecommendedPlatform(p.id, detectedPlatform) ? 'contained' : p.variant}
                  latestVersion={latestVersion}
                  latestRelease={latestRelease}
                  downloading={downloading}
                  onDownload={handleDownload}
                  translate={translate}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          {translate('extensions.eagle_desktop.no_releases')}
        </Alert>
      )}

      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {translate('extensions.eagle_desktop.changelog_title')}
      </Typography>
      <DesktopReleaseTimeline
        releases={data?.releases || []}
        latestVersion={latestVersion}
        translate={translate}
      />
    </Box>
  );
}
